import { z } from 'zod';

// Setter spec §06.7 (WhatsApp window about to expire → one contextual follow-up, never artificial) and §02.j / §06.9
// (WhatsApp-only commitments «escríbeme el jueves a las 4»). Per-project settings stored in Project.followUpConfig;
// values the spec leaves open are settings with the spec's numbers as defaults (owner decision «the spec wins,
// configurable»). Everything provider-facing is fake-tested: «sin verificar».

export const WINDOW_FOLLOW_UP_DEFAULTS = { enabled: true, minutesBefore: 120 } as const;

export const FollowUpTemplateSchema = z
  .object({
    /** Approved WhatsApp template (synced in Integraciones → WhatsApp). */
    name: z.string().trim().min(1).max(200),
    language: z.string().trim().min(2).max(20),
    /** Template variable → text; may use {{contacto.nombre}} / {{proyecto.nombre}}. */
    variables: z.record(z.string().max(40), z.string().max(200)).default({}),
  })
  .strict();
export type FollowUpTemplate = z.infer<typeof FollowUpTemplateSchema>;

export const ProjectFollowUpConfigSchema = z
  .object({
    /** §06.7: one contextual AI follow-up per 24 h window when there is a genuine open thread. */
    windowExpiry: z
      .object({
        enabled: z.boolean().default(WINDOW_FOLLOW_UP_DEFAULTS.enabled),
        /** How long before the window closes the follow-up is attempted (minutes). Night → the last allowed moment before. */
        minutesBefore: z.number().int().min(15).max(600).default(WINDOW_FOLLOW_UP_DEFAULTS.minutesBefore),
      })
      .strict()
      .prefault({}),
    /**
     * §02.j: approved template used when a WhatsApp commitment comes due OUTSIDE the prospect's 24 h window. null = no
     * template: the commitment is recorded as not sent (honest), never a free-form message out of the window.
     */
    commitmentTemplate: FollowUpTemplateSchema.nullable().default(null),
  })
  .strict();
export type ProjectFollowUpConfig = z.infer<typeof ProjectFollowUpConfigSchema>;
export type ProjectFollowUpConfigInput = z.input<typeof ProjectFollowUpConfigSchema>;

export const ProjectFollowUpSettingsSchema = z.object({
  projectId: z.string(),
  config: ProjectFollowUpConfigSchema,
  /** false = nothing saved yet (spec defaults). */
  customized: z.boolean(),
  /** Honest notes (template not synced, no WhatsApp number…). */
  warnings: z.array(z.string()),
  verification: z.string(),
});
export type ProjectFollowUpSettings = z.infer<typeof ProjectFollowUpSettingsSchema>;

export const PutProjectFollowUpSchema = ProjectFollowUpConfigSchema;

export const FOLLOW_UP_VERIFICATION_NOTE =
  'Sin verificar con WhatsApp real: el seguimiento por ventana por vencer y los compromisos por WhatsApp están probados con proveedores simulados.';
